import type {
  Appointment,
  AppointmentEvent,
  AppointmentType,
  ClinicData,
  ClinicTask,
  Conversation,
  FinanceMonth,
  Patient,
} from '../domain/types';
import { tokens } from '../theme/tokens';

function at(dayOffset: number, hour: number, minute = 0): string {
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  date.setDate(date.getDate() + dayOffset);
  return date.toISOString();
}

function dateOnly(yearOffset: number, month: number, day: number): string {
  return new Date(new Date().getFullYear() - yearOffset, month, day, 12).toISOString().slice(0, 10);
}

const appointmentTypes: AppointmentType[] = [
  {
    id: 'type-initial',
    name: 'Primeira consulta',
    durationMinutes: 60,
    defaultPriceCents: 62000,
    calendarColor: '#80556f',
    active: true,
  },
  {
    id: 'type-follow-up',
    name: 'Consulta de acompanhamento',
    durationMinutes: 45,
    defaultPriceCents: 48000,
    calendarColor: '#4f786c',
    active: true,
  },
  {
    id: 'type-online',
    name: 'Teleconsulta',
    durationMinutes: 45,
    defaultPriceCents: 45000,
    calendarColor: '#5d6d92',
    active: true,
  },
  {
    id: 'type-family',
    name: 'Orientação familiar',
    durationMinutes: 30,
    defaultPriceCents: 35000,
    calendarColor: '#9b6b3d',
    active: false,
  },
];

const patients: Patient[] = [
  {
    id: 'patient-ana',
    fullName: 'Ana Lúcia Magalhães',
    birthDate: dateOnly(35, 3, 14),
    cpf: '529.982.247-25',
    cpfDigits: '52998224725',
    primaryPhone: '(11) 98765-4321',
    primaryPhoneDigits: '11987654321',
    secondaryPhone: '',
    emergencyPhone1: '(11) 97654-1000',
    emergencyPhone2: '',
    email: 'ana.magalhaes@example.test',
    observations: '',
    address: {
      postalCode: '01310-100',
      street: 'Avenida Paulista',
      number: '900',
      complement: 'Apto. 82',
      district: 'Bela Vista',
      city: 'São Paulo',
      state: 'SP',
    },
    socialCareEnabled: false,
    socialPricesCents: {},
    requiresAdvancePayment: false,
    outstandingBalanceCents: 18000,
    active: true,
  },
  {
    id: 'patient-beatriz',
    fullName: 'Beatriz Gonçalves Souza',
    birthDate: dateOnly(28, 8, 22),
    cpf: '111.444.777-35',
    cpfDigits: '11144477735',
    primaryPhone: '(11) 99880-2345',
    primaryPhoneDigits: '11998802345',
    secondaryPhone: '(11) 3444-7788',
    emergencyPhone1: '(11) 98811-2233',
    emergencyPhone2: '',
    email: 'beatriz.souza@example.test',
    observations: '',
    address: {
      postalCode: '05407-002',
      street: 'Rua Cardeal Arcoverde',
      number: '1250',
      complement: '',
      district: 'Pinheiros',
      city: 'São Paulo',
      state: 'SP',
    },
    socialCareEnabled: true,
    socialPricesCents: {
      'type-follow-up': 25000,
      'type-online': 23000,
    },
    requiresAdvancePayment: false,
    outstandingBalanceCents: 0,
    active: true,
  },
  {
    id: 'patient-caio',
    fullName: 'Caio Vinícius de Oliveira',
    birthDate: dateOnly(42, 1, 8),
    cpf: '123.456.789-09',
    cpfDigits: '12345678909',
    primaryPhone: '(11) 97712-9000',
    primaryPhoneDigits: '11977129000',
    secondaryPhone: '',
    emergencyPhone1: '(11) 93345-2211',
    emergencyPhone2: '',
    email: 'caio.oliveira@example.test',
    observations: '',
    address: {
      postalCode: '04038-002',
      street: 'Rua Borges Lagoa',
      number: '440',
      complement: 'Casa 2',
      district: 'Vila Clementino',
      city: 'São Paulo',
      state: 'SP',
    },
    socialCareEnabled: false,
    socialPricesCents: {},
    requiresAdvancePayment: true,
    outstandingBalanceCents: 48000,
    active: true,
  },
  {
    id: 'patient-daniela',
    fullName: 'Daniela Ribeiro Mendonça',
    birthDate: dateOnly(31, 10, 3),
    cpf: '390.533.447-05',
    cpfDigits: '39053344705',
    primaryPhone: '(11) 99140-5873',
    primaryPhoneDigits: '11991405873',
    secondaryPhone: '',
    emergencyPhone1: '(11) 92211-0480',
    emergencyPhone2: '(11) 3111-8080',
    email: 'daniela.mendonca@example.test',
    observations: '',
    address: {
      postalCode: '01414-001',
      street: 'Alameda Santos',
      number: '1830',
      complement: '',
      district: 'Cerqueira César',
      city: 'São Paulo',
      state: 'SP',
    },
    socialCareEnabled: false,
    socialPricesCents: {},
    requiresAdvancePayment: false,
    outstandingBalanceCents: 0,
    active: true,
  },
  {
    id: 'patient-eduardo',
    fullName: 'Eduardo Sá Barreto',
    birthDate: dateOnly(47, 5, 27),
    cpf: '987.654.321-00',
    cpfDigits: '98765432100',
    primaryPhone: '(11) 96510-0202',
    primaryPhoneDigits: '11965100202',
    secondaryPhone: '',
    emergencyPhone1: '(11) 97671-2929',
    emergencyPhone2: '',
    email: 'eduardo.barreto@example.test',
    observations: '',
    address: {
      postalCode: '04534-011',
      street: 'Rua Doutor Renato Paes de Barros',
      number: '615',
      complement: 'Conj. 71',
      district: 'Itaim Bibi',
      city: 'São Paulo',
      state: 'SP',
    },
    socialCareEnabled: false,
    socialPricesCents: {},
    requiresAdvancePayment: false,
    outstandingBalanceCents: 0,
    active: true,
  },
];

function event(
  id: string,
  type: AppointmentEvent['type'],
  title: string,
  occurredAt: string,
  detail: string,
): AppointmentEvent {
  return { id, type, title, occurredAt, detail, actorName: 'Marina Costa' };
}

function appointment(
  values: Partial<Appointment> &
    Pick<Appointment, 'id' | 'patientId' | 'patientName' | 'appointmentTypeId' | 'startAt'>,
): Appointment {
  const type =
    appointmentTypes.find((item) => item.id === values.appointmentTypeId) ?? appointmentTypes[0]!;
  const start = new Date(values.startAt);
  const end = new Date(start.getTime() + type.durationMinutes * 60_000);
  const price = values.priceSnapshotCents ?? type.defaultPriceCents;
  const paid = values.amountPaidCents ?? 0;
  const refunded = values.amountRefundedCents ?? 0;
  return {
    id: values.id,
    patientId: values.patientId,
    patientName: values.patientName,
    appointmentTypeId: values.appointmentTypeId,
    appointmentTypeName: type.name,
    providerId: 'user-doctor',
    startAt: values.startAt,
    endAt: end.toISOString(),
    lifecycleStatus: values.lifecycleStatus ?? 'pending_scheduling_confirmation',
    paymentStatus: values.paymentStatus ?? 'pending',
    priceSnapshotCents: price,
    priceSource: values.priceSource ?? 'standard',
    amountPaidCents: paid,
    amountRefundedCents: refunded,
    balanceCents: values.balanceCents ?? Math.max(0, price - paid + refunded),
    expectedPaymentAt: values.expectedPaymentAt ?? '',
    telehealthRoomUrl: values.telehealthRoomUrl ?? 'https://meet.example.test/consulta-segura',
    emailSentAt: values.emailSentAt ?? '',
    color: type.calendarColor,
    events: values.events ?? [
      event(
        `${values.id}-created`,
        'pending_scheduling_confirmation',
        'Aguardando confirmação de agendamento',
        at(-5, 14),
        'Horário reservado.',
      ),
    ],
  };
}

const appointments: Appointment[] = [
  appointment({
    id: 'appointment-today-1',
    patientId: 'patient-ana',
    patientName: 'Ana Lúcia Magalhães',
    appointmentTypeId: 'type-follow-up',
    startAt: at(0, 8),
    lifecycleStatus: 'confirmed',
    amountPaidCents: 30000,
    balanceCents: 18000,
    paymentStatus: 'partially_paid',
    expectedPaymentAt: at(0, 18),
    emailSentAt: at(-1, 10),
  }),
  appointment({
    id: 'appointment-today-2',
    patientId: 'patient-beatriz',
    patientName: 'Beatriz Gonçalves Souza',
    appointmentTypeId: 'type-follow-up',
    startAt: at(0, 9, 30),
    lifecycleStatus: 'pending_scheduling_confirmation',
    priceSnapshotCents: 25000,
    priceSource: 'social',
    balanceCents: 25000,
  }),
  appointment({
    id: 'appointment-today-3',
    patientId: 'patient-daniela',
    patientName: 'Daniela Ribeiro Mendonça',
    appointmentTypeId: 'type-online',
    startAt: at(0, 11),
    lifecycleStatus: 'confirmed',
  }),
  appointment({
    id: 'appointment-tomorrow-1',
    patientId: 'patient-eduardo',
    patientName: 'Eduardo Sá Barreto',
    appointmentTypeId: 'type-initial',
    startAt: at(1, 8),
    lifecycleStatus: 'scheduled',
  }),
  appointment({
    id: 'appointment-tomorrow-2',
    patientId: 'patient-caio',
    patientName: 'Caio Vinícius de Oliveira',
    appointmentTypeId: 'type-follow-up',
    startAt: at(1, 10),
    lifecycleStatus: 'scheduled',
  }),
  appointment({
    id: 'appointment-week-1',
    patientId: 'patient-beatriz',
    patientName: 'Beatriz Gonçalves Souza',
    appointmentTypeId: 'type-online',
    startAt: at(3, 9),
    lifecycleStatus: 'confirmed',
    priceSnapshotCents: 23000,
    priceSource: 'social',
    balanceCents: 23000,
  }),
  appointment({
    id: 'appointment-past-paid',
    patientId: 'patient-daniela',
    patientName: 'Daniela Ribeiro Mendonça',
    appointmentTypeId: 'type-follow-up',
    startAt: at(-7, 9),
    lifecycleStatus: 'completed',
    amountPaidCents: 48000,
    balanceCents: 0,
    paymentStatus: 'paid',
    events: [
      event('e1', 'completed', 'Consulta realizada', at(-7, 10), 'Atendimento finalizado.'),
      event(
        'e2',
        'payment',
        'Pagamento registrado',
        at(-7, 10, 5),
        'Pagamento integral de R$ 480,00.',
      ),
    ],
  }),
  appointment({
    id: 'appointment-past-no-show',
    patientId: 'patient-caio',
    patientName: 'Caio Vinícius de Oliveira',
    appointmentTypeId: 'type-follow-up',
    startAt: at(-14, 10),
    lifecycleStatus: 'no_show',
    balanceCents: 48000,
    events: [
      event(
        'e3',
        'no_show',
        'Paciente faltou',
        at(-14, 10, 50),
        'Pagamento antecipado exigido para novas consultas.',
      ),
    ],
  }),
  appointment({
    id: 'appointment-past-refund',
    patientId: 'patient-ana',
    patientName: 'Ana Lúcia Magalhães',
    appointmentTypeId: 'type-initial',
    startAt: at(-28, 8),
    lifecycleStatus: 'cancelled',
    amountPaidCents: 62000,
    amountRefundedCents: 22000,
    balanceCents: 0,
    paymentStatus: 'partially_refunded',
    events: [
      event(
        'e4',
        'cancelled',
        'Consulta cancelada',
        at(-30, 11),
        'Cancelamento registrado com preservação do histórico.',
      ),
      event('e5', 'refund', 'Devolução registrada', at(-29, 12), 'Devolução parcial de R$ 220,00.'),
    ],
  }),
  appointment({
    id: 'appointment-rescheduled',
    patientId: 'patient-eduardo',
    patientName: 'Eduardo Sá Barreto',
    appointmentTypeId: 'type-online',
    startAt: at(-20, 9),
    lifecycleStatus: 'rescheduled',
    events: [
      event(
        'e6',
        'rescheduled',
        'Consulta reagendada',
        at(-22, 16),
        'De 12/07/2026 às 09:00 para novo horário.',
      ),
    ],
  }),
];

function task(values: Partial<ClinicTask> & Pick<ClinicTask, 'id' | 'title'>): ClinicTask {
  const assigneeId = values.assigneeId ?? 'user-doctor';
  const assigneeName = assigneeId === 'user-doctor' ? 'Dra. Priscilla Falcão' : 'Marina Costa';
  return {
    id: values.id,
    title: values.title,
    description: values.description ?? '',
    creatorId: values.creatorId ?? 'user-secretary',
    creatorName: values.creatorName ?? 'Marina Costa',
    assigneeId,
    assigneeName,
    status: values.status ?? 'pending',
    priority: values.priority ?? 'normal',
    dueAt: values.dueAt ?? '',
    createdAt: values.createdAt ?? at(-3, 14),
    updatedAt: values.updatedAt ?? at(-1, 9),
    completedAt: values.completedAt ?? '',
    patientId: values.patientId ?? '',
    appointmentId: values.appointmentId ?? '',
    activity: values.activity ?? [
      {
        id: `${values.id}-created`,
        occurredAt: at(-3, 14),
        actorName: values.creatorName ?? 'Marina Costa',
        text: `Tarefa atribuída a ${assigneeName}.`,
      },
    ],
  };
}

const tasks: ClinicTask[] = [
  task({
    id: 'task-1',
    title: 'Atualizar receita',
    description: 'Verificar a solicitação antes do retorno da paciente.',
    dueAt: at(0, 12),
    priority: 'high',
    patientId: 'patient-ana',
  }),
  task({
    id: 'task-2',
    title: 'Revisar disponibilidade de setembro',
    dueAt: at(2, 16),
    status: 'in_progress',
    creatorId: 'user-doctor',
    creatorName: 'Dra. Priscilla Falcão',
    assigneeId: 'user-secretary',
    priority: 'normal',
  }),
  task({
    id: 'task-3',
    title: 'Retornar confirmação de agenda',
    dueAt: at(-1, 17),
    assigneeId: 'user-secretary',
    priority: 'urgent',
  }),
  task({
    id: 'task-4',
    title: 'Conferir dados cadastrais',
    dueAt: at(-4, 15),
    status: 'completed',
    completedAt: at(-4, 14),
    assigneeId: 'user-doctor',
    patientId: 'patient-beatriz',
  }),
];

const conversations: Conversation[] = [
  {
    id: 'conversation-clinic',
    title: 'Operação do consultório',
    type: 'clinic',
    participantIds: ['user-doctor', 'user-secretary'],
    lastMessageAt: at(0, 7, 51),
    lastMessagePreview: 'Deixei também uma tarefa sobre a atualização pendente.',
    unreadByUser: { 'user-doctor': 2, 'user-secretary': 0 },
    messages: [
      {
        id: 'message-1',
        senderId: 'user-secretary',
        senderName: 'Marina Costa',
        text: 'Bom dia! As confirmações de amanhã já estão separadas.',
        sentAt: at(0, 7, 42),
        status: 'sent',
        attachment: null,
      },
      {
        id: 'message-2',
        senderId: 'user-doctor',
        senderName: 'Dra. Priscilla Falcão',
        text: 'Ótimo, obrigada. Vou revisar a agenda entre os atendimentos.',
        sentAt: at(0, 7, 48),
        status: 'sent',
        attachment: null,
      },
      {
        id: 'message-3',
        senderId: 'user-secretary',
        senderName: 'Marina Costa',
        text: 'Deixei também uma tarefa sobre a atualização pendente.',
        sentAt: at(0, 7, 51),
        status: 'sent',
        attachment: null,
      },
    ],
  },
  {
    id: 'conversation-direct',
    title: 'Marina Costa',
    type: 'direct',
    participantIds: ['user-doctor', 'user-secretary'],
    lastMessageAt: at(-1, 17, 10),
    lastMessagePreview: 'Podemos manter o bloqueio da sexta-feira até 11h.',
    unreadByUser: { 'user-doctor': 0, 'user-secretary': 1 },
    messages: [
      {
        id: 'message-4',
        senderId: 'user-doctor',
        senderName: 'Dra. Priscilla Falcão',
        text: 'Podemos manter o bloqueio da sexta-feira até 11h.',
        sentAt: at(-1, 17, 10),
        status: 'sent',
        attachment: null,
      },
    ],
  },
];

function createFinanceMonths(): FinanceMonth[] {
  const labels = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  const forecast = [1240, 1680, 1540, 2010, 1880, 2240, 2160, 2380, 2050, 2470, 2300, 2520];
  return labels.map((month, index) => {
    const forecastCents = (forecast[index] ?? 0) * 100;
    const grossReceivedCents = Math.round(
      forecastCents * (index > new Date().getMonth() ? 0 : 0.82),
    );
    const refundCents = index % 4 === 0 ? 22000 : 0;
    return {
      month,
      forecastCents,
      grossReceivedCents,
      refundCents,
      netReceivedCents: grossReceivedCents - refundCents,
    };
  });
}

export function createDemoData(): ClinicData {
  return {
    users: [
      {
        id: 'user-doctor',
        clinicId: 'clinic-demo',
        displayName: 'Dra. Priscilla Falcão',
        email: 'medica@consultorio.test',
        role: 'medico',
        avatarColor: tokens.color.doctorAvatar,
      },
      {
        id: 'user-secretary',
        clinicId: 'clinic-demo',
        displayName: 'Marina Costa',
        email: 'secretaria@consultorio.test',
        role: 'secretaria',
        avatarColor: tokens.color.secretaryAvatar,
      },
    ],
    patients,
    appointmentTypes,
    availabilityRules: [1, 2, 3, 4, 5].map((weekday) => ({
      id: `availability-${weekday}`,
      providerId: 'user-doctor',
      weekday,
      startMinute: 8 * 60,
      endMinute: 12 * 60,
      validFromLocalDate: '2020-01-01',
      validUntilLocalDate: '',
      active: true,
    })),
    appointments,
    scheduleBlocks: [
      {
        id: 'block-1',
        startAt: at(2, 10),
        endAt: at(2, 11),
        reason: 'Compromisso externo',
        status: 'active',
        createdBy: 'Dra. Priscilla Falcão',
        updatedBy: 'Dra. Priscilla Falcão',
      },
    ],
    tasks,
    conversations,
    notifications: [
      {
        id: 'notification-1',
        type: 'task',
        title: 'Nova tarefa atribuída',
        detail: 'Atualizar receita foi atribuída a você.',
        createdAt: at(-1, 9),
        read: false,
        href: '/tarefas',
      },
      {
        id: 'notification-2',
        type: 'appointment',
        title: 'Aguardando confirmação',
        detail: 'Há consultas de amanhã aguardando confirmação.',
        createdAt: at(-1, 16),
        read: false,
        href: '/agenda',
      },
      {
        id: 'notification-3',
        type: 'payment',
        title: 'Pagamento esperado hoje',
        detail: 'Um saldo combinado tem vencimento hoje.',
        createdAt: at(0, 8),
        read: false,
        href: '/financeiro',
      },
    ],
    financeMonths: createFinanceMonths(),
  };
}
